/**
 * C2 (wave 7, 2026-09-19) — the lifecycle of the post-force convergence poll (hooks.ts's
 * `convergeSweepThenSettle`), owned by the QUERY CLIENT, not by any screen.
 *
 * WHY THE CLIENT: the poll converges the shared query cache after a real force/bulk-force, so a dialog
 * closing (the screen that started it) must NOT cut it off. But it must die with the cache it
 * converges. Before C2 nothing cancelled it at all: it kept calling window.setTimeout after the tree
 * that owned the cache was gone (in tests: into a torn-down jsdom realm).
 *
 * One `AbortController` per QueryClient (a WeakMap entry, so a discarded client takes its entry with
 * it). It is aborted — every pending timer cleared, every in-flight poll dropped, NO settle — when:
 *   - `client.clear()` runs (the cache it would invalidate is gone), or
 *   - `client.unmount()` runs, i.e. a QueryClientProvider holding this client unmounts (the tree
 *     that reads the cache is being torn down; React Query calls this from the provider's effect
 *     cleanup — including @testing-library's per-test cleanup).
 * After an abort the entry is reset, so a client that is mounted again / reused after clear() starts
 * its next loop on a fresh, un-aborted controller.
 *
 * ACCOUNT SWITCH: this app has no in-place identity switch — sign-out (and session-expiry "sign in
 * again") is an Auth0 redirect, a full page navigation, which destroys the realm and every timer in
 * it. Any future in-place switch must call `queryClient.clear()` (the other user's cached rows must
 * not survive anyway), and that aborts the loop through the first rule above.
 *
 * NEVER DOUBLE-RUNS: at most ONE loop per client. A second force while a loop is in flight SUPERSEDES
 * it: the old loop is cancelled and its settle callbacks are carried into the new one, which takes a
 * fresh baseline. Keeping the old baseline would be wrong — a sweep round that completed between the
 * two forces would settle, and the refetch would bring back the pre-second-force membership (the
 * Licensed→Basic→Licensed flap the whole convergence exists to prevent). Carrying the settles means
 * nothing the first caller asked to invalidate is dropped; they run once, together, when the newest
 * loop settles.
 */
import type { QueryClient } from "@tanstack/react-query";

export type ConvergeSettle = () => void;

type Entry = {
  controller: AbortController;
  /** Settle callbacks owed by the loop currently in flight (empty when none is). */
  pending: ConvergeSettle[];
  /** Aborts ONLY the loop in flight (supersede), never the client's lifecycle controller. */
  loop: AbortController | null;
};

const registry = new WeakMap<QueryClient, Entry>();

function install(qc: QueryClient): Entry {
  const entry: Entry = { controller: new AbortController(), pending: [], loop: null };
  registry.set(qc, entry);
  const tearDown = () => {
    const e = registry.get(qc);
    if (!e) return;
    e.controller.abort();
    e.loop?.abort();
    e.loop = null;
    e.pending = [];
    // Fresh controller: the client may be mounted again (StrictMode, a re-rendered provider) or keep
    // being used after clear(); the next loop must not start pre-aborted.
    e.controller = new AbortController();
  };
  const origClear = qc.clear.bind(qc);
  qc.clear = () => {
    tearDown();
    origClear();
  };
  const origUnmount = qc.unmount.bind(qc);
  qc.unmount = () => {
    tearDown();
    origUnmount();
  };
  return entry;
}

function entryFor(qc: QueryClient): Entry {
  return registry.get(qc) ?? install(qc);
}

/**
 * Start (or supersede) the one convergence loop for `qc`. `run` receives a signal that aborts when the
 * loop is superseded or the client is cleared/unmounted, and a `settle` to call exactly once when it
 * is done; settle is a no-op once the signal has aborted, so a torn-down or superseded loop can never
 * fire into the cache.
 */
export function startConvergeLoop(
  qc: QueryClient,
  settleCb: ConvergeSettle,
  run: (signal: AbortSignal, settle: () => void) => void,
): void {
  const entry = entryFor(qc);
  const carried = entry.pending;
  entry.loop?.abort(); // supersede — its settles are carried below, never dropped
  const loop = new AbortController();
  const lifecycle = entry.controller.signal;
  // Auto-removed when THIS loop aborts (supersede, or its own settle below), so superseded loops
  // never pile listeners onto the client's long-lived controller.
  lifecycle.addEventListener("abort", () => loop.abort(), { once: true, signal: loop.signal });
  entry.loop = loop;
  entry.pending = [...carried, settleCb];
  let settled = false;
  const settle = () => {
    if (settled || loop.signal.aborted) return;
    settled = true;
    const due = entry.pending;
    entry.pending = [];
    entry.loop = null;
    loop.abort(); // done: detaches the lifecycle listener; nothing of this loop may run after this
    for (const cb of due) cb();
  };
  run(loop.signal, settle);
}

/** Test/diagnostic seam: is a convergence loop currently in flight for this client? */
export function isConvergeLoopActive(qc: QueryClient): boolean {
  const e = registry.get(qc);
  return !!e?.loop && !e.loop.signal.aborted;
}
